import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { Icon } from '../../atoms'
import { IconProps } from '../../atoms/icons/icon'
import {
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselShadcn,
} from '../../../shadcn/components/ui/carousel'
import { cn } from '../../../shadcn/utils'

interface PictureCarouselProps {
  urls: string[]
  fallbackSvg?: IconProps['name']
}
export const PictureCarousel = ({
  urls,
  fallbackSvg,
}: PictureCarouselProps) => {
  const [cApi, setCApi] = useState<CarouselApi>()
  const [slideIndex, setSlideIndex] = useState(0)

  useEffect(() => {
    if (cApi) {
      cApi.on('select', () => {
        setSlideIndex(cApi.selectedScrollSnap())
      })
    }
  }, [cApi])

  const renderDots = () => {
    const maxVisibleDots = 5
    const totalSlides = urls.length
    const dotWidth = 8
    const gapWidth = 8
    const dotClassNames =
      'h-2 w-2 cursor-pointer rounded-full bg-white transition-opacity duration-200'

    // If we have 5 or fewer slides, render all dots without sliding
    if (totalSlides <= maxVisibleDots) {
      return (
        <div className='flex gap-2'>
          {Array.from({ length: totalSlides }, (_, i) => (
            <button
              key={i}
              className={cn(dotClassNames, {
                'opacity-50': slideIndex !== i,
              })}
              onClick={() => cApi?.scrollTo(i)}
            />
          ))}
        </div>
      )
    }

    // For more than 5 slides, render all dots but slide them when selected picture past the 3rd dot
    return (
      <div
        className='flex gap-2 transition-transform duration-300'
        style={{
          transform: `translateX(${Math.max(
            Math.min(
              -slideIndex * (dotWidth + gapWidth) + (dotWidth + gapWidth) * 2,
              0,
            ),
            -((totalSlides - maxVisibleDots) * (dotWidth + gapWidth)),
          )}px)`,
        }}
      >
        {Array.from({ length: totalSlides }, (_, i) => (
          <button
            key={i}
            className={cn(dotClassNames, {
              'opacity-50': slideIndex !== i,
            })}
            onClick={() => cApi?.scrollTo(i)}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      {urls.length ? (
        <CarouselShadcn className='group h-full' setApi={setCApi}>
          <CarouselContent containerClassName={cn('h-full')}>
            {urls.map((url, index) => (
              <CarouselItem
                key={index}
                className={cn(
                  'flex h-full items-center justify-center',
                  '@2xl:max-h-none',
                )}
              >
                <Image
                  src={url}
                  alt='house'
                  width={1920}
                  height={1080}
                  className='h-full w-full object-cover'
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {urls.length > 1 && (
            <>
              <CarouselPrevious
                className={cn(
                  'absolute left-2 top-1/2',
                  '@4xl:hidden group-hover:@4xl:inline-flex',
                )}
              />
              <CarouselNext
                className={cn(
                  'absolute right-2 top-1/2',
                  '@4xl:hidden group-hover:@4xl:inline-flex',
                )}
              />
              <div
                className={cn(
                  'absolute bottom-2 left-1/2 -translate-x-1/2',
                  '@4xl:hidden group-hover:@4xl:block',
                )}
              >
                <div
                  className={cn('flex overflow-hidden', {
                    ['w-[72px]']: urls.length >= 5,
                  })}
                >
                  {renderDots()}
                </div>
              </div>
            </>
          )}
        </CarouselShadcn>
      ) : (
        <div className='flex h-full items-center justify-center bg-grey-800'>
          <Icon
            name={fallbackSvg ?? 'happy-team'}
            size={60}
            color='var(--brand-400)'
          />
        </div>
      )}
    </>
  )
}
